import Alert from "antd/lib/alert";
import Badge from "antd/lib/badge";
import Button from "antd/lib/button";
import Card from "antd/lib/card";
import Col from "antd/lib/col";
import Empty from "antd/lib/empty";
import PageHeader from "antd/lib/page-header";
import Row from "antd/lib/row";
import Space from "antd/lib/space";
import Statistic from "antd/lib/statistic";
import Tabs from "antd/lib/tabs";
import Typography from "antd/lib/typography";
import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import Helmet from "react-helmet";
import { useNavigate } from "react-router-dom";
import ContactSubmissionList from "~components/ContactSubmissionList";
import UserTable from "~components/UserTable";
import config from "~config";
import LayoutContext from "~context/Layout";
import UserContext from "~context/User";
import ContactSubmission from "~types/ContactSubmission";
import PaginatedUsers from "~types/PaginatedUsers";
import SendGridStats from "~types/SendGridStats";
import User from "~types/User";
import sleep from "~utilities/sleep";
import { UserSortBy, UserSortOrder } from "~components/UserTable/UserTable.types";

type Overview = {
    totalOrphanedRegistrations: number;
    totalUsers: number;
    totalNewUsersLast7Days: number;
    totalUnresolvedContactSubmissions: number;
    totalUnresolvedContactSubmissionsOver24Hours: number;
    totalUnresolvedContactSubmissionsOver72Hours: number;
};

type RepairDoorAccessJob = {
    id: string;
    status: "running" | "completed" | "failed" | "cancelled";
    cancelRequested: boolean;
    dryRun: boolean;
    includeUsersWithAccess: boolean;
    batchSize: number;
    batchIntervalMs: number;
    batchMaxRetries: number;
    startedAt: number;
    finishedAt?: number;
    error?: string;
    candidatesConsidered: number;
    processedCandidates: number;
    entitledCandidates: number;
    skippedNotEntitled: number;
    skippedAlreadyHasAccess: number;
    repaired: number;
    failed: number;
    batchesProcessed: number;
};

function AdministrationPage() {
    const supportedTabs = [
        "overview",
        "users",
        "contact-submissions",
        "devices",
        "sengrid",
    ] as const;
    const persistedTabKey = localStorage.getItem("administration-tabs-active-key");
    const initialTabKey =
        persistedTabKey && supportedTabs.includes(persistedTabKey as (typeof supportedTabs)[number])
            ? persistedTabKey
            : "overview";

    const navigate = useNavigate();
    const layout = useContext(LayoutContext);
    const user = useContext(UserContext);
    const initializedForAdminRef = useRef<boolean>(false);

    const [tabsActiveKey, setTabsActiveKey] = useState<string>(
        initialTabKey
    );

    const [overview, setOverview] = useState<Overview>();
    const [overviewFetching, setOverviewFetching] = useState<boolean>(false);
    const [overviewError, setOverviewError] = useState<string>();
    const [repairDoorAccessRunning, setRepairDoorAccessRunning] =
        useState<boolean>(false);
    const [repairDoorAccessMessage, setRepairDoorAccessMessage] =
        useState<string>();
    const [repairDoorAccessJobId, setRepairDoorAccessJobId] =
        useState<string>();

    const [users, setUsers] = useState<User[]>();
    const [usersFetching, setUsersFetching] = useState<boolean>(false);
    const [usersPage, setUsersPage] = useState<number>(1);
    const [usersPageSize, setUsersPageSize] = useState<number>(50);
    const [usersTotal, setUsersTotal] = useState<number>(0);
    const [usersSortBy, setUsersSortBy] = useState<UserSortBy>();
    const [usersSortOrder, setUsersSortOrder] =
        useState<UserSortOrder>();

    const [contactSubmissions, setContactSubmissions] =
        useState<ContactSubmission[]>();
    const [contactSubmissionsFetching, setContactSubmissionsFetching] =
        useState<boolean>(false);

    const [devices, setDevices] = useState<object>();
    const [devicesFetching, setDevicesFetching] = useState<boolean>(false);

    const [sendGridStats, setSendGridStats] = useState<SendGridStats>();
    const [sendGridStatsFetching, setSendGridStatsFetching] =
        useState<boolean>(false);

    useEffect(() => {
        if (layout.data && layout.actions) {
            if (layout.data.header !== true) layout.actions.updateHeader(true);
            if (layout.data.footer !== true) layout.actions.updateFooter(true);
        }
    }, [layout.actions, layout.data]);

    useEffect(() => {
        if (!user.data) return;

        if (!user.data.administrator) {
            navigate("/");
            return;
        }

        if (!initializedForAdminRef.current) {
            initializedForAdminRef.current = true;
            getOverview();
            loadTabDataForKey(tabsActiveKey);
        }
    }, [user.data?.id, user.data?.administrator, navigate]);

    useEffect(() => {
        localStorage.setItem("administration-tabs-active-key", tabsActiveKey);
        loadTabDataForKey(tabsActiveKey);
    }, [tabsActiveKey]);

    useEffect(() => {
        if (!repairDoorAccessRunning) return;

        const intervalId = window.setInterval(() => {
            void getRepairDoorAccessStatus(repairDoorAccessJobId);
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [repairDoorAccessRunning, repairDoorAccessJobId]);

    const unresolvedCount =
        overview?.totalUnresolvedContactSubmissions ||
        contactSubmissions?.filter((o) => o.resolved !== true).length ||
        0;
    const userCount = overview?.totalUsers || usersTotal || users?.length || 0;
    const pageRefreshing =
        overviewFetching ||
        usersFetching ||
        contactSubmissionsFetching ||
        devicesFetching ||
        sendGridStatsFetching;

    const attentionItems: string[] = [];
    if ((overview?.totalUnresolvedContactSubmissionsOver72Hours || 0) > 0)
        attentionItems.push(
            `${overview?.totalUnresolvedContactSubmissionsOver72Hours} customer messages are older than 3 days`
        );
    return (
        <Fragment>
            <Helmet>
                <title>Administration - Twenty4 Tan Co.</title>
            </Helmet>

            <div className="container-xl my-50 px-50">
                {localStorage.getItem("administration-alert") === null && (
                    <Alert
                        className="mb-50 br-10"
                        closable={true}
                        closeText="Dismiss"
                        description="Only you and any other administrators can view this page."
                        message="Restricted page"
                        type="warning"
                        onClose={() =>
                            localStorage.setItem("administration-alert", "")
                        }
                    />
                )}
                <Card
                    className="br-10 bs-grey-5"
                    loading={overviewFetching}
                    title={
                        <PageHeader
                            className="p-0"
                            title="Administration"
                            subTitle="Track daily operations and follow-up tasks."
                            extra={[
                                <Button
                                    key="refresh-admin"
                                    className="br-5"
                                    loading={pageRefreshing}
                                    onClick={() => refreshAll()}
                                >
                                    Refresh
                                </Button>,
                            ]}
                        />
                    }
                >
                    <Tabs
                        activeKey={tabsActiveKey}
                        onChange={(activeKey) => setTabsActiveKey(activeKey)}
                    >
                        <Tabs.TabPane key="overview" tab="Overview">
                            {overviewError && (
                                <Alert
                                    className="mb-10"
                                    message="We could not load this summary"
                                    description={overviewError}
                                    showIcon={true}
                                    type="error"
                                />
                            )}
                            {overview && (
                                <Alert
                                    className="mb-10"
                                    message={
                                        attentionItems.length > 0
                                            ? "Needs attention"
                                            : "All clear"
                                    }
                                    description={
                                        attentionItems.length > 0
                                            ? attentionItems.join(". ")
                                            : "No urgent issues right now."
                                    }
                                    showIcon={true}
                                    type={
                                        attentionItems.length > 0
                                            ? "warning"
                                            : "success"
                                    }
                                />
                            )}
                            {repairDoorAccessMessage && (
                                <Alert
                                    className="mb-10"
                                    message={repairDoorAccessMessage}
                                    showIcon={true}
                                    type="info"
                                />
                            )}
                            <Row
                                align="stretch"
                                gutter={15}
                                style={{
                                    marginTop: "-10px",
                                    marginBottom: "-10px",
                                }}
                            >
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="Sign-ups not finished"
                                            value={
                                                overview?.totalOrphanedRegistrations
                                            }
                                            valueRender={(node) => (
                                                <span
                                                    className={
                                                        overview?.totalOrphanedRegistrations ===
                                                        0
                                                            ? "c-success"
                                                            : "c-warning"
                                                    }
                                                >
                                                    {node}
                                                </span>
                                            )}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            People who started signing up but
                                            have not finished yet.
                                        </Typography.Paragraph>
                                    </Card>
                                </Col>
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="Total members"
                                            value={overview?.totalUsers}
                                            valueRender={(node) => (
                                                <span className="c-success">
                                                    {node}
                                                </span>
                                            )}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            Total accounts in the system.
                                        </Typography.Paragraph>
                                    </Card>
                                </Col>
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="Open customer messages"
                                            value={
                                                overview?.totalUnresolvedContactSubmissions
                                            }
                                            valueRender={(node) => (
                                                <span
                                                    className={
                                                        overview?.totalUnresolvedContactSubmissions ===
                                                        0
                                                            ? "c-success"
                                                            : "c-warning"
                                                    }
                                                >
                                                    {node}
                                                </span>
                                            )}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            Messages from customers that still
                                            need a response.
                                        </Typography.Paragraph>
                                    </Card>
                                </Col>
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="Open messages older than 1 day"
                                            value={
                                                overview?.totalUnresolvedContactSubmissionsOver24Hours
                                            }
                                            valueRender={(node) => (
                                                <span
                                                    className={
                                                        (overview?.totalUnresolvedContactSubmissionsOver24Hours ||
                                                            0) === 0
                                                            ? "c-success"
                                                            : "c-warning"
                                                    }
                                                >
                                                    {node}
                                                </span>
                                            )}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            Customer messages waiting more than
                                            24 hours.
                                        </Typography.Paragraph>
                                    </Card>
                                </Col>
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="Open messages older than 3 days"
                                            value={
                                                overview?.totalUnresolvedContactSubmissionsOver72Hours
                                            }
                                            valueRender={(node) => (
                                                <span
                                                    className={
                                                        (overview?.totalUnresolvedContactSubmissionsOver72Hours ||
                                                            0) === 0
                                                            ? "c-success"
                                                            : "c-warning"
                                                    }
                                                >
                                                    {node}
                                                </span>
                                            )}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            Long-wait customer messages that
                                            need priority follow-up.
                                        </Typography.Paragraph>
                                    </Card>
                                </Col>
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="New members (last 7 days)"
                                            value={
                                                overview?.totalNewUsersLast7Days
                                            }
                                            valueRender={(node) => (
                                                <span
                                                    className={
                                                        (overview?.totalNewUsersLast7Days ||
                                                            0) > 0
                                                            ? "c-success"
                                                            : "c-warning"
                                                    }
                                                >
                                                    {node}
                                                </span>
                                            )}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            Accounts created in the last 7 days.
                                        </Typography.Paragraph>
                                    </Card>
                                </Col>
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="Repair missing door access"
                                            value={repairDoorAccessRunning ? "Running..." : "Ready"}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            Use this to recover members that are missing door access.
                                        </Typography.Paragraph>
                                        <Space>
                                            <Button
                                                className="br-5"
                                                disabled={repairDoorAccessRunning}
                                                loading={repairDoorAccessRunning}
                                                size="small"
                                                type="primary"
                                                onClick={() => repairDoorAccess()}
                                            >
                                                Repair Door Access
                                            </Button>
                                            {repairDoorAccessRunning && (
                                                <Button
                                                    className="br-5"
                                                    danger={true}
                                                    size="small"
                                                    onClick={() => cancelRepairDoorAccess()}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </Space>
                                    </Card>
                                </Col>
                                <Col
                                    className="my-10"
                                    style={{ height: "inherit !important" }}
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={8}
                                >
                                    <Card className="h-100 br-10">
                                        <Statistic
                                            loading={overviewFetching}
                                            title="Repair result"
                                            value={repairDoorAccessMessage ? "Updated" : "No run yet"}
                                        />
                                        <Typography.Paragraph
                                            className="mb-0 font-size-xs"
                                            type="secondary"
                                        >
                                            The latest repair status appears above after each run.
                                        </Typography.Paragraph>
                                    </Card>
                                </Col>
                            </Row>
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            key="users"
                            tab={
                                <Space align="center" size={6}>
                                    <span>Users</span>
                                    <Badge
                                        count={userCount}
                                        overflowCount={9999}
                                        showZero={true}
                                    />
                                </Space>
                            }
                        >
                            <Card
                                bodyStyle={{
                                    padding: "0",
                                }}
                            >
                                <UserTable
                                    users={users}
                                    loading={usersFetching}
                                    page={usersPage}
                                    pageSize={usersPageSize}
                                    total={usersTotal}
                                    sortBy={usersSortBy}
                                    sortOrder={usersSortOrder}
                                    onTableChange={(
                                        page,
                                        pageSize,
                                        sortBy,
                                        sortOrder
                                    ) => {
                                        setUsersPage(page);
                                        setUsersPageSize(pageSize);
                                        setUsersSortBy(sortBy);
                                        setUsersSortOrder(sortOrder);
                                        getUsers(
                                            page,
                                            pageSize,
                                            sortBy,
                                            sortOrder
                                        );
                                    }}
                                />
                            </Card>
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            key="contact-submissions"
                            tab={
                                <Space align="center" size={6}>
                                    <span>Contact Submissions</span>
                                    <Badge
                                        count={unresolvedCount}
                                        overflowCount={999}
                                        showZero={true}
                                        status={
                                            unresolvedCount > 0
                                                ? "warning"
                                                : "success"
                                        }
                                    />
                                </Space>
                            }
                        >
                            <ContactSubmissionList
                                contactSubmissions={contactSubmissions}
                                loading={contactSubmissionsFetching}
                                onUpdate={(contactSubmission) => {
                                    if (contactSubmissions) {
                                        const otherContactSubmissions =
                                            contactSubmissions.filter(
                                                (o) =>
                                                    o.id !==
                                                    contactSubmission.id
                                            );
                                        setContactSubmissions([
                                            contactSubmission,
                                            ...otherContactSubmissions,
                                        ]);
                                    } else
                                        setContactSubmissions([
                                            contactSubmission,
                                        ]);
                                    if (overview) {
                                        setOverview({
                                            ...overview,
                                            totalUnresolvedContactSubmissions:
                                                overview.totalUnresolvedContactSubmissions +
                                                (contactSubmission.resolved
                                                    ? -1
                                                    : 1),
                                        });
                                    }
                                }}
                                onDelete={(id) => {
                                    if (contactSubmissions) {
                                        const otherContactSubmissions =
                                            contactSubmissions.filter(
                                                (o) => o.id !== id
                                            );
                                        setContactSubmissions(
                                            otherContactSubmissions
                                        );
                                    }
                                }}
                            />
                        </Tabs.TabPane>
                        <Tabs.TabPane key="devices" tab="RemoteLock">
                            <Card
                                bodyStyle={{ padding: "16px" }}
                                className="br-10"
                                loading={devicesFetching}
                            >
                                <Typography.Paragraph type="secondary">
                                    Live device payload from the RemoteLock API.
                                </Typography.Paragraph>
                                {devices ? (
                                    <pre
                                        style={{
                                            margin: 0,
                                            maxHeight: "420px",
                                            overflow: "auto",
                                            background: "#fafafa",
                                            borderRadius: "8px",
                                            padding: "12px",
                                        }}
                                    >
                                        {JSON.stringify(devices, null, 4)}
                                    </pre>
                                ) : (
                                    <Empty description="No RemoteLock device data" />
                                )}
                            </Card>
                        </Tabs.TabPane>
                        <Tabs.TabPane key="sengrid" tab="SendGrid">
                            <Card
                                bodyStyle={{ padding: "16px" }}
                                className="br-10"
                                loading={sendGridStatsFetching}
                            >
                                <Typography.Paragraph type="secondary">
                                    Recent SendGrid delivery and engagement stats.
                                </Typography.Paragraph>
                                {sendGridStats ? (
                                    <pre
                                        style={{
                                            margin: 0,
                                            maxHeight: "420px",
                                            overflow: "auto",
                                            background: "#fafafa",
                                            borderRadius: "8px",
                                            padding: "12px",
                                        }}
                                    >
                                        {JSON.stringify(sendGridStats, null, 4)}
                                    </pre>
                                ) : (
                                    <Empty description="No SendGrid data" />
                                )}
                            </Card>
                        </Tabs.TabPane>
                    </Tabs>
                </Card>
            </div>
        </Fragment>
    );

    async function getOverview(forceRefresh = false) {
        if (!overviewFetching) {
            setOverviewFetching(true);
            if (overviewError) setOverviewError(undefined);
            try {
                const queryString = forceRefresh
                    ? "?forceRefresh=true"
                    : "";
                const response = await fetch(
                    `${config.TWENTY4TANCO_API_BASE_URL}/admin/overview${queryString}`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                        },
                        credentials: "include",
                    }
                );

                const result: HttpResult<Overview> = await response.json();
                if (result.status === "success" && result.data)
                    setOverview(result.data);
                else
                    setOverviewError(
                        result.message || "Failed to get overview"
                    );
            } catch (e) {
                console.error(e);
            }
            setOverviewFetching(false);
        }
    }

    async function repairDoorAccess() {
        if (repairDoorAccessRunning) return;

        setRepairDoorAccessMessage(undefined);

        try {
            const startResponse = await fetch(
                `${config.TWENTY4TANCO_API_BASE_URL}/admin/repair-door-access?dryRun=false`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include",
                }
            );

            const startResult: HttpResult<{
                started: boolean;
                message: string;
                job: RepairDoorAccessJob;
            }> = await startResponse.json();

            if (
                startResult.status !== "success" ||
                !startResult.data ||
                !startResult.data.job
            ) {
                setRepairDoorAccessMessage(
                    startResult.message || "Failed to start door access repair."
                );
                return;
            }

            const job = startResult.data.job;
            setRepairDoorAccessJobId(job.id);
            applyRepairDoorAccessJobStatus(job);
        } catch (e) {
            console.error(e);
            setRepairDoorAccessMessage(
                "Failed to run door access repair."
            );
        }
    }

    async function cancelRepairDoorAccess() {
        if (!repairDoorAccessRunning) return;

        try {
            const params = new URLSearchParams();
            if (repairDoorAccessJobId)
                params.set("jobId", repairDoorAccessJobId);

            const response = await fetch(
                `${config.TWENTY4TANCO_API_BASE_URL}/admin/repair-door-access/cancel?${params.toString()}`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include",
                }
            );

            const result: HttpResult<{
                cancelled: boolean;
                message: string;
                job: RepairDoorAccessJob;
            }> = await response.json();

            if (result.status === "success" && result.data?.job) {
                applyRepairDoorAccessJobStatus(result.data.job);
                setRepairDoorAccessMessage("Cancellation requested. Waiting for current batch to stop...");
            } else {
                setRepairDoorAccessMessage(
                    result.message || "Failed to cancel repair job."
                );
            }
        } catch (e) {
            console.error(e);
            setRepairDoorAccessMessage("Failed to cancel repair job.");
        }
    }

    async function getRepairDoorAccessStatus(jobId?: string) {
        try {
            const params = new URLSearchParams();
            if (jobId) params.set("jobId", jobId);

            const query = params.toString();
            const response = await fetch(
                `${config.TWENTY4TANCO_API_BASE_URL}/admin/repair-door-access/status${
                    query ? `?${query}` : ""
                }`,
                {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include",
                }
            );

            const result: HttpResult<RepairDoorAccessJob> =
                await response.json();

            if (result.status === "success" && result.data) {
                setRepairDoorAccessJobId(result.data.id);
                applyRepairDoorAccessJobStatus(result.data);
            } else if (!repairDoorAccessMessage) {
                setRepairDoorAccessRunning(false);
            }
        } catch (e) {
            console.error(e);
        }
    }

    function applyRepairDoorAccessJobStatus(job: RepairDoorAccessJob) {
        if (job.status === "running") {
            setRepairDoorAccessRunning(true);
            setRepairDoorAccessMessage(
                `Repair running: batch ${job.batchesProcessed}, processed ${job.processedCandidates}/${job.candidatesConsidered || "?"}, repaired ${job.repaired}, failed ${job.failed}${
                    job.cancelRequested
                        ? ", cancellation requested"
                        : ""
                }.`
            );
            return;
        }

        setRepairDoorAccessRunning(false);

        if (job.status === "completed") {
            setRepairDoorAccessMessage(
                `Repair complete: repaired ${job.repaired}, failed ${job.failed}, entitled candidates ${job.entitledCandidates}, checked ${job.candidatesConsidered}.`
            );
            getOverview(true);
            getUsers(usersPage, usersPageSize, usersSortBy, usersSortOrder);
            return;
        }

        if (job.status === "cancelled") {
            setRepairDoorAccessMessage(
                `Repair cancelled: processed ${job.processedCandidates}/${job.candidatesConsidered || "?"}, repaired ${job.repaired}, failed ${job.failed}.`
            );
            getOverview(true);
            getUsers(usersPage, usersPageSize, usersSortBy, usersSortOrder);
            return;
        }

        setRepairDoorAccessMessage(
            `Repair failed${job.error ? `: ${job.error}` : "."} Processed ${job.processedCandidates}/${job.candidatesConsidered || "?"}, repaired ${job.repaired}, failed ${job.failed}.`
        );
        getOverview(true);
        getUsers(usersPage, usersPageSize, usersSortBy, usersSortOrder);
    }

    async function getUsers(
        page = usersPage,
        pageSize = usersPageSize,
        sortBy = usersSortBy,
        sortOrder = usersSortOrder
    ) {
        if (!usersFetching) {
            setUsersFetching(true);
            try {
                const params = new URLSearchParams({
                    page: String(page),
                    pageSize: String(pageSize),
                });
                if (sortBy) params.set("sortBy", sortBy);
                if (sortOrder) params.set("sortOrder", sortOrder);

                const response = await fetch(
                    `${config.TWENTY4TANCO_API_BASE_URL}/admin/users?${params.toString()}`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                        },
                        credentials: "include",
                    }
                );

                const result: HttpResult<PaginatedUsers> =
                    await response.json();
                if (result.status === "success" && result.data) {
                    setUsers(result.data.users);
                    setUsersPage(result.data.page);
                    setUsersPageSize(result.data.pageSize);
                    setUsersTotal(result.data.total);
                    setUsersSortBy(sortBy);
                    setUsersSortOrder(sortOrder);
                }
            } catch (e) {
                console.error(e);
            }
            setUsersFetching(false);
        }
    }

    async function getContactSubmissions() {
        if (!contactSubmissionsFetching) {
            setContactSubmissionsFetching(true);
            await sleep(1);
            try {
                const response = await fetch(
                    `${config.TWENTY4TANCO_API_BASE_URL}/admin/contact-submissions`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                        },
                        credentials: "include",
                    }
                );

                const result: HttpResult<ContactSubmission[]> =
                    await response.json();
                if (result.status === "success" && result.data)
                    setContactSubmissions(result.data);
            } catch (e) {
                console.error(e);
            }
            setContactSubmissionsFetching(false);
        }
    }

    async function getDevices() {
        if (!devicesFetching) {
            setDevicesFetching(true);
            await sleep(1);
            try {
                const response = await fetch(
                    `${config.TWENTY4TANCO_API_BASE_URL}/admin/devices`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                        },
                        credentials: "include",
                    }
                );

                const result: HttpResult<object> = await response.json();
                if (result.status === "success" && result.data)
                    setDevices(result.data);
            } catch (e) {
                console.error(e);
            }
            setDevicesFetching(false);
        }
    }

    async function getSendGridStats() {
        if (!sendGridStatsFetching) {
            setSendGridStatsFetching(true);
            await sleep(1);
            try {
                const response = await fetch(
                    `${config.TWENTY4TANCO_API_BASE_URL}/admin/sendgrid/stats`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                        },
                        credentials: "include",
                    }
                );

                const result: HttpResult<SendGridStats> = await response.json();
                if (result.status === "success" && result.data)
                    setSendGridStats(result.data);
            } catch (e) {
                console.error(e);
            }
            setSendGridStatsFetching(false);
        }
    }

    function refreshAll() {
        getOverview(true);
        getRepairDoorAccessStatus(repairDoorAccessJobId);
        getUsers(usersPage, usersPageSize);
        getContactSubmissions();
        getDevices();
        getSendGridStats();
    }

    function loadTabDataForKey(tabKey: string) {
        if (tabKey === "users") {
            if (!users && !usersFetching) getUsers(1, usersPageSize);
            return;
        }

        if (tabKey === "contact-submissions") {
            if (!contactSubmissions && !contactSubmissionsFetching)
                getContactSubmissions();
            return;
        }

        if (tabKey === "devices") {
            if (!devices && !devicesFetching) getDevices();
            return;
        }

        if (tabKey === "sengrid") {
            if (!sendGridStats && !sendGridStatsFetching) getSendGridStats();
            return;
        }

        if (!repairDoorAccessMessage) {
            getRepairDoorAccessStatus(repairDoorAccessJobId);
        }

    }
}

export default AdministrationPage;
